/*
 * The public listener card (Skins, mockup 34, ENG-733), opened from a name in
 * the stream chat. Backend ENG-734 serves it as
 * `GET /public/users/{userId}/card?bandId=` (schema `ListenerCard`):
 *
 *   { userId, displayName, avatarUrl ('' for none), namePaint | null,
 *     regularAt: { bandId, bandName, tier: 'regular' | 'superfan' } | null,
 *     skinCount, wearing: ListenerCardSkin | null, skins: ListenerCardSkin[] }
 *
 *   ListenerCardSkin = { skin: { id, name, type, rarity, editionSize?, paint?, assets },
 *                        editionNumber?, earned, equipped }
 *
 * The helpers here tidy that payload and pick the label each skin carries.
 */
import config from '../config.js';
import { fetchWrapper } from './fetchWrapper.js';

const RARITIES = ['common', 'rare', 'epic', 'legendary'];

const positiveInt = (value) => {
  const n = Number(value);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
};

/** One `ListenerCardSkin`, checked; null when it has no usable skin. */
export const normalizeCardSkin = (input) => {
  const skin = input?.skin;
  if (!skin || typeof skin !== 'object' || typeof skin.id !== 'string' || !skin.id) return null;
  return {
    skin: {
      id: skin.id,
      name: typeof skin.name === 'string' ? skin.name : '',
      type: typeof skin.type === 'string' ? skin.type : '',
      rarity: RARITIES.includes(skin.rarity) ? skin.rarity : 'common',
      editionSize: positiveInt(skin.editionSize),
      paint: skin.paint && typeof skin.paint === 'object' ? skin.paint : null,
      // Render data as stored; a now-playing card skin draws from it (cardSkin.js, ENG-735).
      assets: skin.assets && typeof skin.assets === 'object' && !Array.isArray(skin.assets) ? skin.assets : {},
      // A vibe set's or boost animation's FX package (ENG-815); only FX items carry one.
      ...(typeof skin.fxPackageId === 'string' && skin.fxPackageId ? { fxPackageId: skin.fxPackageId } : {}),
      // A vibe set's suggested boost, which its try-on previews (ENG-866).
      ...(typeof skin.defaultBoostPackageId === 'string' && skin.defaultBoostPackageId
        ? { defaultBoostPackageId: skin.defaultBoostPackageId }
        : {}),
    },
    editionNumber: positiveInt(input.editionNumber),
    earned: input.earned === true,
    equipped: input.equipped === true,
  };
};

/** The `ListenerCard` payload, checked and tidied; null when it is not one. */
export const normalizeListenerCard = (input) => {
  if (!input || typeof input !== 'object') return null;
  const displayName = typeof input.displayName === 'string' ? input.displayName : '';
  if (!displayName) return null;
  const regular = input.regularAt;
  const skins = Array.isArray(input.skins) ? input.skins.map(normalizeCardSkin).filter(Boolean) : [];
  const skinCount = Number.isSafeInteger(Number(input.skinCount)) && Number(input.skinCount) >= 0
    ? Math.max(Number(input.skinCount), skins.length)
    : skins.length;
  return {
    userId: positiveInt(input.userId),
    displayName,
    avatarUrl: typeof input.avatarUrl === 'string' ? input.avatarUrl : '',
    namePaint: input.namePaint && typeof input.namePaint === 'object' ? input.namePaint : null,
    regularAt: regular && typeof regular === 'object' && typeof regular.bandName === 'string' && regular.bandName
      ? { bandId: positiveInt(regular.bandId), bandName: regular.bandName, tier: regular.tier === 'superfan' ? 'superfan' : 'regular' }
      : null,
    skinCount,
    wearing: normalizeCardSkin(input.wearing),
    skins,
  };
};

/**
 * The label a skin carries on the card. The data model has earned, limited
 * editions and rarity; it has no "seasonal" or "standard" flag, so:
 *   - earned -> 'earned'
 *   - a limited edition (`editionSize`) -> 'limited', with the copy number
 *   - an unlimited common skin -> 'standard'
 *   - any other unlimited skin -> its rarity ('rare', 'epic', 'legendary')
 * "Seasonal" is not shown until the catalog says which skins are seasonal.
 */
export const skinTag = (cardSkin) => {
  if (!cardSkin) return null;
  if (cardSkin.earned) return { kind: 'earned' };
  if (cardSkin.skin.editionSize) {
    return { kind: 'limited', editionNumber: cardSkin.editionNumber, editionSize: cardSkin.skin.editionSize };
  }
  if (cardSkin.skin.rarity === 'common') return { kind: 'standard' };
  return { kind: cardSkin.skin.rarity };
};

/** Two-letter initials for the avatar stand-in ("BackRowBecky" -> "BR", "sam" -> "SA"). */
export const nameInitials = (name) => {
  const text = String(name || '').trim();
  if (!text) return '?';
  const words = text.split(/[\s_.-]+/).filter(Boolean);
  if (words.length > 1) return (Array.from(words[0])[0] + Array.from(words[1])[0]).toUpperCase();
  const capitals = Array.from(text).filter((ch) => ch !== ch.toLowerCase());
  if (capitals.length >= 2) return (capitals[0] + capitals[1]).toUpperCase();
  return Array.from(text).slice(0, 2).join('').toUpperCase();
};

/**
 * Fetches `userId`'s card for the stream page of `bandId` (optional). Rejects
 * with the fetchWrapper error: 404 `user_not_found` for an unknown or deleted
 * account, 400 `invalid_request`, 503 when the service isn't configured.
 */
export const fetchListenerCard = async (userId, bandId) => {
  const user = positiveInt(userId);
  if (!user) return Promise.reject({ status: 400, code: 'invalid_request', message: 'invalid user id' });
  const band = positiveInt(bandId);
  const data = band
    ? await fetchWrapper.get(`${config.apiBaseUrl}/public/users/${user}/card?bandId=${band}`)
    : await fetchWrapper.get(`${config.apiBaseUrl}/public/users/${user}/card`);
  const card = normalizeListenerCard(data);
  if (!card) return Promise.reject({ status: 500, code: null, message: 'malformed listener card' });
  return card;
};
